import { useQueryClient } from '@tanstack/react-query';
import { compact, includes, sortBy, uniq } from 'es-toolkit/compat';

import {
  archiveJobExperimentResults,
  getJobExperimentResultSummaries,
  getJobExperimentResultSummary,
  restoreJobExperimentResults,
} from 'api/jobs';
import {
  JobExperimentResultDetailedSummary,
  JobExperimentResultArchiveOperation,
  JobExperimentResultSummariesResponse,
} from 'interfaces/jobExperimentResults';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import { useQueryWithErrorAndRetrySnackbar } from 'utils/useQueryWithErrorAndRetrySnackbar';
import { useCurrentLabId } from 'utils/useCurrentLab';

export const jobExperimentResultSummariesQueryKey = 'jobExperimentResultSummaries';
export const jobExperimentResultDetailQueryKey = 'jobExperimentResultDetail';

export const normalizeJobExperimentResultSummaryIds = (jobIds: string[]) => sortBy(uniq(compact(jobIds)));

export const getJobExperimentResultSummariesQueryKey = (labId: string, jobIds: string[]) =>
  [jobExperimentResultSummariesQueryKey, labId, normalizeJobExperimentResultSummaryIds(jobIds)] as const;

export const getJobExperimentResultDetailQueryKey = (labId: string, jobId: string) =>
  [jobExperimentResultDetailQueryKey, labId, jobId] as const;

export const useJobExperimentResultSummaries = (jobIds: string[], enabled = true) => {
  const { labId } = useCurrentLabId();
  const normalizedJobIds = normalizeJobExperimentResultSummaryIds(jobIds);
  const canQuery = enabled && Boolean(labId) && normalizedJobIds.length > 0;

  return useQueryWithErrorAndRetrySnackbar<JobExperimentResultSummariesResponse>(
    getJobExperimentResultSummariesQueryKey(labId, normalizedJobIds),
    ({ signal }) => getJobExperimentResultSummaries(labId, normalizedJobIds, signal),
    {
      enabled: canQuery,
      queriedEntityName: 'job experiment results',
      keepPreviousData: true,
    }
  );
};

export const useJobExperimentResultDetail = (jobId: string, enabled = true) => {
  const { labId } = useCurrentLabId();
  const canQuery = enabled && Boolean(labId) && Boolean(jobId);

  return useQueryWithErrorAndRetrySnackbar<JobExperimentResultDetailedSummary>(
    getJobExperimentResultDetailQueryKey(labId, jobId),
    ({ signal }) => getJobExperimentResultSummary(labId, jobId, signal),
    {
      enabled: canQuery,
      queriedEntityName: 'job experiment results',
      retry: false,
    }
  );
};

interface JobExperimentResultArchiveMutationVariables {
  jobId: string;
  operation: JobExperimentResultArchiveOperation;
}

const compactSummaryQueryContainsJob = (queryKey: readonly unknown[], labId: string, jobId: string) =>
  queryKey[0] === jobExperimentResultSummariesQueryKey &&
  queryKey[1] === labId &&
  Array.isArray(queryKey[2]) &&
  includes(queryKey[2], jobId);

export const useJobExperimentResultArchiveMutation = () => {
  const { labId } = useCurrentLabId();
  const queryClient = useQueryClient();

  return useMutationWithErrorSnackbar<JobExperimentResultDetailedSummary, JobExperimentResultArchiveMutationVariables>({
    mutationDescription: 'update job experiment results',
    mutationFn: ({ jobId, operation }) =>
      operation === 'archive' ? archiveJobExperimentResults(labId, jobId) : restoreJobExperimentResults(labId, jobId),
    onSuccess: (summary, { jobId }) => {
      queryClient.setQueryData(getJobExperimentResultDetailQueryKey(labId, jobId), summary);
      void queryClient.invalidateQueries({
        predicate: ({ queryKey }) => compactSummaryQueryContainsJob(queryKey, labId, jobId),
      });
    },
  });
};
